<template>
  <div class="home mt-2">
    <!-- Encabezado unificado con tarjeta colapsable de filtros -->
    <entity-header
      title="Billing Sequences"
      :show-new="isAdmin()"
      new-label="Nueva Billing Sequence"
      new-tooltip="Nueva Billing Sequence"
      :has-active-filters="hasActiveFilters"
      @export="getBillingSequencesExcel"
      @new="showEdit({}, 'add')"
      @search="serviciosBuscar()"
    >
      <template #filters>
        <div class="form-group col mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="servicio_select">Por Sequence</label>
          <input
            type="text"
            class="form-control"
            id="servicio_select"
            v-model="servicio_sel"
            placeholder="Filtrar por sequence..."
            @keyup.enter="serviciosBuscar()"
            aria-label="Servicio"
          />
        </div>
        <div class="form-group col mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="descripcion_select">Por Descripción</label>
          <input
            type="text"
            class="form-control"
            id="descripcion_select"
            v-model="descripcion_sel"
            placeholder="Filtrar por descripción..."
            @keyup.enter="serviciosBuscar()"
            aria-label="Descripción"
          />
        </div>
        <div class="form-group col-md-3 mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="enabled_select">Por Estado</label>
          <select
            class="form-control"
            v-model="activo_sel"
            name="atributo"
            aria-label="Activo"
            id="enabled_select"
          >
            <option selected :value="null">Todos los estados</option>
            <option :value="0">Inactivo</option>
            <option :value="1">Activo</option>
          </select>
        </div>
      </template>
    </entity-header>

    <div class="alert alert-success" role="alert" v-if="success">{{ success }}</div>
    <div class="alert alert-danger" role="alert" v-if="error">{{ error }}</div>

    <!-- Tabla de datos estandarizada con acciones -->
    <data-table
      v-if="billingSequencesBusqueda && billingSequencesBusqueda.data && billingSequencesBusqueda.data.length"
      :columns="columns"
      :data="billingSequencesBusqueda.data"
      :order-by-field="orderByField"
      :order-by-direction="orderByDirection"
      :has-actions="isAdmin()"
      id-key="id_billing_sequence"
      @sort="orderByfield"
    >
      <template #cell-integrador="{ item, value }">
        {{ value || getIntegradorName(item.id_integrador) }}
      </template>

      <template #actions="{ item }">
        <table-actions
          :item="item"
          active-key="activo"
          @edit="showEdit(item, 'edit')"
          @toggle="showEdit(item, 'toggle')"
        />
      </template>
    </data-table>

    <!-- Paginador y estado vacío -->
    <Paginador
      v-if="billingSequencesBusqueda && billingSequencesBusqueda.pages"
      :pages="billingSequencesBusqueda.pages"
      @selPage="changePage"
    ></Paginador>

    <empty-state
      :show="Boolean(post && !(billingSequencesBusqueda && billingSequencesBusqueda.data && billingSequencesBusqueda.data.length))"
      message="No se encontraron secuencias de facturación con el criterio seleccionado."
    />

    <!-- Ventana Modal Estandarizada -->
    <crud-modal
      :show="showModal"
      :title="modalTitle"
      :is-toggle="operation == 'toggle'"
      :show-save="operation == 'edit' || operation == 'add' || operation == 'toggle'"
      :save-text="modalSaveText"
      :save-variant="operation == 'toggle' ? (editData && editData.activo == 1 ? 'warning' : 'success') : 'primary'"
      @close="closeModal"
      @save="onModalSave"
    >
      <form v-if="editData && (operation == 'edit' || operation == 'add')">
        <div v-if="operation == 'add' && isTodosIntegrador" class="form-group">
          <label class="font-weight-bold small text-muted mb-1">Integrador <span class="text-danger">*</span></label>
          <select
            class="form-control"
            name="id_integrador"
            :class="{ 'is-invalid': hasFieldError('id_integrador') }"
            v-model="editData.id_integrador"
            required
          >
            <option :value="null">Seleccione un integrador...</option>
            <option
              v-for="item in integradoresList"
              :key="item.id_integrador || item.id"
              :value="item.id_integrador || item.id"
            >
              {{ item.integrador }}
            </option>
          </select>
          <small class="form-text text-muted">Seleccione el integrador para esta secuencia.</small>
        </div>
        <div v-else-if="operation == 'edit'" class="form-group">
          <label class="font-weight-bold small text-muted mb-1">Integrador</label>
          <input
            type="text"
            class="form-control"
            :value="editData.integrador || getIntegradorName(editData.id_integrador)"
            disabled
            readonly
          />
          <small class="form-text text-muted">Integrador al que pertenece la secuencia (no editable).</small>
        </div>
        <div v-else-if="operation == 'add'" class="form-group">
          <label class="font-weight-bold small text-muted mb-1">Integrador</label>
          <input
            type="text"
            class="form-control"
            :value="getIntegradorName(id_integrador_sel)"
            disabled
            readonly
          />
          <small class="form-text text-muted">Integrador seleccionado para la nueva secuencia (no editable).</small>
        </div>

        <div class="form-group">
          <label class="font-weight-bold small text-muted mb-1">Secuencia <span class="text-danger">*</span></label>
          <div class="input-group">
            <input
              class="form-control"
              :class="{ 'is-invalid': hasFieldError('sequence') }"
              name="sequence"
              placeholder="Ej: 1/2-3 o 1{2}-3"
              type="text"
              v-model="editData.sequence"
              @keydown="onSequenceKeyDown"
              @input="onSequenceInput"
              required
            />
            <div class="input-group-append">
              <button
                type="button"
                class="btn btn-outline-primary"
                title="Editar de forma gráfica"
                @click="openSequenceModal"
              >
                <i class="bi bi-diagram-3 mr-1"></i>Constructor Gráfico
              </button>
            </div>
          </div>
          <small class="form-text text-muted">Cadena de IDs de billing codes o edítela con el Constructor Gráfico.</small>
        </div>

        <div class="form-group">
          <label class="font-weight-bold small text-muted mb-1">Descripción</label>
          <input
            class="form-control"
            :class="{ 'is-invalid': hasFieldError('descripcion') }"
            name="descripcion"
            placeholder="Ej: Secuencia Estándar Diaria"
            type="text"
            v-model="editData.descripcion"
          />
          <small class="form-text text-muted">Nombre o detalle descriptivo de la secuencia. (Se puede utilizar el placeholder {PERIODO_FACTURADO} Ej: 03/11/2026 al 03/12/2026) </small>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="font-weight-bold small text-muted mb-1">Precio sin Impuestos</label>
            <price-input
              name="full_price"
              :input-class="{ 'is-invalid': hasFieldError('full_price') }"
              placeholder="0.00"
              v-model="editData.full_price"
            />
            <small class="form-text text-muted">Monto base sin impuestos (número decimal, ej: 10.00).</small>
          </div>

          <div class="form-group col-md-4">
            <label class="font-weight-bold small text-muted mb-1">Precio con Impuestos</label>
            <price-input
              name="full_price_tax"
              :input-class="{ 'is-invalid': hasFieldError('full_price_tax') }"
              placeholder="0.00"
              v-model="editData.full_price_tax"
            />
            <small class="form-text text-muted">Monto total final con impuestos incluidos (número decimal, ej: 11.80).</small>
          </div>

          <div class="form-group col-md-4">
            <label class="font-weight-bold small text-muted mb-1">Precio Mínimo Sin Imp</label>
            <price-input
              name="min_price_bs"
              :input-class="{ 'is-invalid': hasFieldError('min_price_bs') }"
              placeholder="0.00"
              v-model="editData.min_price_bs"
            />
            <small class="form-text text-muted">Precio mínimo aceptado en la secuencia (número decimal, ej: 0.00).</small>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="font-weight-bold small text-muted mb-1">Restricciones</label>
            <input
              class="form-control"
              :class="{ 'is-invalid': hasFieldError('bill_restriction') }"
              name="bill_restriction"
              placeholder="Ej: 1:1 o 2:1"
              type="text"
              v-model="editData.bill_restriction"
            />
            <small class="form-text text-muted">Regla de facturación en formato Intentos:Días (ej: 1:1, 2:1).</small>
          </div>

          <div class="form-group col-md-4">
            <label class="font-weight-bold small text-muted mb-1">Frecuencia</label>
            <input
              class="form-control"
              :class="{ 'is-invalid': hasFieldError('frecuency') }"
              name="frecuency"
              placeholder="Ej: 1"
              type="text"
              v-model="editData.frecuency"
            />
            <small class="form-text text-muted">Frecuencia o periodicidad de reintento en días (número entero, ej: 1).</small>
          </div>

          <div class="form-group col-md-4">
            <label class="font-weight-bold small text-muted mb-1">Lifetime</label>
            <input
              class="form-control"
              :class="{ 'is-invalid': hasFieldError('lifetime') }"
              name="lifetime"
              placeholder="Ej: 24"
              type="text"
              v-model="editData.lifetime"
            />
            <small class="form-text text-muted">Tiempo de vida de la secuencia en horas (número entero, ej: 24).</small>
          </div>
        </div>

        <div v-if="errors" class="alert alert-danger" role="alert">
          <div v-if="typeof errors === 'string'">{{ errors }}</div>
          <div v-else>
            <div class="font-weight-bold mb-1">Por favor corrija los siguientes campos:</div>
            <ul class="mb-0 pl-3">
              <li v-for="(err, field) in errors" :key="field">
                <strong>{{ formatFieldName(field) }}:</strong> {{ formatErrorRule(field, err) }}
              </li>
            </ul>
          </div>
        </div>
      </form>
    </crud-modal>

    <!-- Modal del Constructor Gráfico de Secuencia -->
    <crud-modal
      :show="showSequenceModal"
      title="Constructor Gráfico de Secuencia"
      size="xl"
      :show-save="true"
      save-text="Aplicar Secuencia"
      cancel-text="Cancelar"
      nested
      @close="closeSequenceModal"
      @save="applySequenceModal"
    >
      <sequence-builder
        v-if="showSequenceModal"
        v-model="tempSequence"
        :id-integrador="editData ? (editData.id_integrador || (isTodosIntegrador ? null : id_integrador_sel)) : null"
        @suggest-prices="onSuggestPrices"
      />
    </crud-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { entityCrudMixin } from "@/mixins/entityCrudMixin";
import { startAsyncExport } from "@/utils/exportHelper";
import EntityHeader from "./utiles/EntityHeader.vue";
import DataTable from "./utiles/DataTable.vue";
import TableActions from "./utiles/TableActions.vue";
import EmptyState from "./utiles/EmptyState.vue";
import Paginador from "./Paginador.vue";
import CrudModal from "./utiles/CrudModal.vue";
import PriceInput from "./utiles/PriceInput.vue";
import SequenceBuilder from "./billing/SequenceBuilder.vue";

export default {
  name: "BillingSequences",
  mixins: [entityCrudMixin],

  components: {
    EntityHeader,
    DataTable,
    TableActions,
    EmptyState,
    Paginador,
    CrudModal,
    PriceInput,
    SequenceBuilder,
  },

  data() {
    return {
      columns: [
        { label: "Id", field: "id_billing_sequence", width: "80" },
        { label: "Integrador", field: "integrador" },
        { label: "Secuencia", field: "sequence" },
        { label: "Descripción", field: "descripcion" },
        {
          label: "Precio Sin Imp",
          field: "full_price",
          formatter: (val) => this.formatPrice(val),
        },
        {
          label: "Precio Con Imp",
          field: "full_price_tax",
          formatter: (val) => this.formatPrice(val),
        },
        { label: "Restricción", field: "bill_restriction" },
        { label: "Estado", field: "activo", type: "badge", width: "110" },
      ],
      success: null,
      error: null,
      billingsequences: null,
      descripcion_sel: null,
      activo_sel: null,
      id_integrador_sel: localStorage.getItem("integrador"),
      servicio_sel: null,
      billingSequencesBusqueda: null,
      pages: null,
      showSequenceModal: false,
      tempSequence: "",
    };
  },

  created() {
    this.orderByField = "id_billing_sequence";
    this.getBillingSequencesBusqueda();
  },

  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),

    modalTitle() {
      if (!this.editData) return "";
      if (this.operation === "toggle") {
        return this.editData.activo == 1
          ? "Suspender Billing Sequence"
          : "Reactivar Billing Sequence";
      }
      if (this.operation === "edit") {
        return `Editar Billing Sequence ${this.editData.id_billing_sequence || ""}`;
      }
      if (this.operation === "add") {
        return "Nueva Billing Sequence";
      }
      return "";
    },

    modalSaveText() {
      if (this.operation === "toggle") {
        return this.editData && this.editData.activo == 1 ? "Suspender" : "Reactivar";
      }
      return "Guardar";
    },
    hasActiveFilters() {
      return (
        Boolean(this.servicio_sel) ||
        Boolean(this.descripcion_sel) ||
        (this.activo_sel !== null && this.activo_sel !== undefined)
      );
    },

    isTodosIntegrador() {
      return !this.id_integrador_sel || this.id_integrador_sel === "todos";
    },

    integradoresList() {
      let list = [];
      if (this.integradores && this.integradores.data && this.integradores.data.length > 0) {
        list = [...this.integradores.data];
      } else if (this.user && this.user.integradores && Array.isArray(this.user.integradores)) {
        list = [...this.user.integradores];
      }
      return list.sort((a, b) =>
        String(a.integrador || "").localeCompare(String(b.integrador || ""), undefined, { sensitivity: "base" })
      );
    },
  },

  watch: {
    "$store.state.integrador.integrador"(newVal) {
      this.id_integrador_sel = newVal;
      this.page = 1;
      this.getBillingSequencesBusqueda();
    },
  },

  methods: {
    showEdit(item = {}, operation = "edit") {
      this.errors = null;
      this.operation = operation;
      this.editData = Object.assign({}, item);
      if (operation === "add") {
        this.editData.id_integrador = this.isTodosIntegrador
          ? null
          : parseInt(this.id_integrador_sel);
      }
      this.showModal = true;
    },

    formatPrice(val) {
      if (val === null || val === undefined || val === "") return "-";
      const cleaned = String(val).replace(/^\$\s*/, "").trim();
      const num = Number(cleaned);
      if (isNaN(num)) return cleaned;
      return num.toFixed(2);
    },

    getIntegradorName(id) {
      if (!id) return "";
      const list = this.integradores && this.integradores.data ? this.integradores.data : (Array.isArray(this.integradores) ? this.integradores : []);
      const found = list.find((i) => String(i.id_integrador || i.id) === String(id));
      return found ? found.integrador : id;
    },
    buscar(hideResult = false) {
      this.getBillingSequencesBusqueda(hideResult);
    },

    serviciosBuscar() {
      this.changePage(1);
    },

    openSequenceModal() {
      if (!this.editData) return;
      const integId =
        this.editData.id_integrador ||
        (this.isTodosIntegrador ? null : this.id_integrador_sel);
      if (!integId) {
        Swal.fire({
          title: "Atención",
          text: "Seleccione un integrador antes de abrir el constructor gráfico.",
          icon: "warning",
          confirmButtonText: "Aceptar",
        });
        return;
      }
      this.tempSequence = String(this.editData.sequence || "").replace(/[^0-9/{}-]/g, "");
      this.showSequenceModal = true;
    },

    onSequenceKeyDown(e) {
      const allowedControlKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];
      if (
        allowedControlKeys.includes(e.key) ||
        e.ctrlKey ||
        e.metaKey ||
        e.altKey
      ) {
        return;
      }
      if (!/^[0-9/{}-]$/.test(e.key)) {
        e.preventDefault();
      }
    },

    onSequenceInput(e) {
      if (!this.editData) return;
      const raw = e && e.target ? e.target.value : this.editData.sequence || "";
      const cleaned = String(raw).replace(/[^0-9/{}-]/g, "");
      if (cleaned !== raw && e && e.target) {
        e.target.value = cleaned;
      }
      this.$set(this.editData, "sequence", cleaned);
    },

    closeSequenceModal() {
      this.showSequenceModal = false;
    },

    applySequenceModal() {
      if (this.editData) {
        this.$set(this.editData, "sequence", this.tempSequence);
      }
      this.showSequenceModal = false;
    },

    onSuggestPrices(prices) {
      if (!this.editData || !prices) return;
      if (prices.full_price !== null && prices.full_price !== undefined) {
        this.$set(this.editData, "full_price", String(prices.full_price));
      }
      if (prices.full_price_tax !== null && prices.full_price_tax !== undefined) {
        this.$set(this.editData, "full_price_tax", String(prices.full_price_tax));
      }
      if (prices.min_price_bs !== null && prices.min_price_bs !== undefined) {
        this.$set(this.editData, "min_price_bs", String(prices.min_price_bs));
      }
    },

    onModalSave() {
      if (this.operation === "toggle") return this.toggleStatus();
      if (this.operation === "edit") return this.editServicio();
      if (this.operation === "add") return this.agregarServicio();
    },

    getBillingSequencesBusqueda(hideResult = true) {
      if (hideResult) this.billingSequencesBusqueda = null;
      this.post = false;
      axios
        .post(process.env.VUE_APP_API_URL + "billingsequence/busqueda", {
          page: this.page,
          id_integrador: this.id_integrador_sel,
          sequence: this.servicio_sel,
          descripcion: this.descripcion_sel,
          activo: this.activo_sel,
          order_by: { field: this.orderByField, order: this.orderByDirection },
        })
        .then((response) => {
          this.billingSequencesBusqueda = response.data;
          this.post = true;
          if (
            this.billingSequencesBusqueda.data &&
            this.billingSequencesBusqueda.data.length == 0
          ) {
            this.error = null;
          }
        })
        .catch(() => {
          this.post = true;
          Swal.fire({
            title: "Error!",
            text: "Ha ocurrido un error al buscar las secuencias de facturación",
            icon: "error",
            confirmButtonText: "Aceptar",
          });
        });
    },

    agregarServicio() {
      this.errors = null;
      if (!this.editData.id_integrador || this.editData.id_integrador === "todos") {
        if (!this.isTodosIntegrador) {
          this.editData.id_integrador = parseInt(this.id_integrador_sel);
        } else {
          this.errors = "Debe seleccionar un integrador para la nueva secuencia";
          return;
        }
      }
      this.$store.commit("setLoading", true);
      axios
        .post(process.env.VUE_APP_API_URL + "billingsequence/new", this.editData)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error;
          } else {
            this.closeModal();
            Swal.fire({
              title: "Bien!",
              text: "Billing Sequence creada correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && (e.response.data.error || e.response.data.errors)) {
            this.errors = e.response.data.error || e.response.data.errors;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar crear Billing Sequence",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          if (this.operation == null) this.getBillingSequencesBusqueda();
        });
    },

    editServicio() {
      this.errors = null;
      this.$store.commit("setLoading", true);
      if (!this.editData.id_integrador || this.editData.id_integrador === "todos") {
        if (!this.isTodosIntegrador) {
          this.editData.id_integrador = parseInt(this.id_integrador_sel);
        }
      }
      axios
        .post(process.env.VUE_APP_API_URL + "billingsequence/update", this.editData)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error;
          } else {
            this.closeModal();
            Swal.fire({
              title: "Bien!",
              text: "Billing Sequence actualizada correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && (e.response.data.error || e.response.data.errors)) {
            this.errors = e.response.data.error || e.response.data.errors;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar guardar",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          if (this.operation == null) this.getBillingSequencesBusqueda();
        });
    },

    toggleStatus() {
      this.errors = null;
      this.$store.commit("setLoading", true);
      const targetActivo = 1 - Number(this.editData.activo || 0);
      const payload = {
        ...this.editData,
        activo: targetActivo,
      };
      axios
        .post(process.env.VUE_APP_API_URL + "billingsequence/update", payload)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error;
          } else {
            this.closeModal();
            Swal.fire({
              title: "Bien!",
              text: "Billing Sequence actualizada correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errors = e.response.data.error;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar guardar",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          if (this.operation == null) this.getBillingSequencesBusqueda();
        });
    },

    getBillingSequencesExcel() {
      startAsyncExport(
        "billingsequence",
        {
          id_integrador: this.id_integrador_sel,
          sequence: this.servicio_sel,
          descripcion: this.descripcion_sel,
          activo: this.activo_sel,
          order_by: { field: this.orderByField, order: this.orderByDirection },
        },
        "BillingSequences"
      );
    },

    isAdmin() {
      return this.user && this.user.rol == "admin";
    },

    formatFieldName(field) {
      const fieldNames = {
        id_integrador: "Integrador",
        sequence: "Secuencia",
        descripcion: "Descripción",
        full_price: "Precio sin Impuestos",
        precio_sin_imp: "Precio sin Impuestos",
        full_price_tax: "Precio (con Impuestos)",
        precio_con_imp: "Precio (con Impuestos)",
        min_price_bs: "Precio Mínimo Sin Imp",
        precio_min: "Precio Mínimo Sin Imp",
        lifetime: "Lifetime",
        frecuency: "Frecuencia",
        bill_restriction: "Restricciones",
        activo: "Estado",
      };
      return fieldNames[field] || field;
    },

    formatErrorRule(field, err) {
      const errList = Array.isArray(err) ? err : [err];
      const messages = errList.map((rule) => {
        if (rule === "required") return "Campo obligatorio";
        if (rule === "regex") {
          if (field === "bill_restriction") return "Formato inválido. Debe ser X:Y (ej: 1:1)";
          if (field === "sequence") return "Formato inválido. Debe ser N o N/N-N (ej: 1/2-3)";
          if (
            field === "full_price" ||
            field === "precio_sin_imp" ||
            field === "full_price_tax" ||
            field === "precio_con_imp" ||
            field === "min_price_bs" ||
            field === "precio_min"
          ) {
            return "Debe ser un valor numérico decimal (ej: 10.00)";
          }
          return "Formato no válido";
        }
        if (rule === "integer") return "Debe ser un número entero";
        if (rule === "number") return "Debe ser un valor numérico";
        return rule;
      });
      return messages.join(", ");
    },
  },
};
</script>

<style scoped></style>
